import { Injectable } from "@angular/core";
import { BehaviorSubject, from, Observable, of, ReplaySubject } from "rxjs";
import { AngularFireAuth } from "@angular/fire/auth";

import {
  AngularFirestore,
  AngularFirestoreCollection,
  AngularFirestoreDocument,
  CollectionReference,
  Query,
} from "@angular/fire/firestore";
import { HttpClient } from "@angular/common/http";
import * as firebase from "firebase/app";
import "firebase/firestore";
import { AngularFireFunctions } from "@angular/fire/functions";
import { User, UserData } from "../data/users";
import { environment } from "../../../environments/environment";
import Shop from "../../models/shop.model";

@Injectable()
export class UserService {
  baseUrl = environment.apiUrl;
  companyId = environment.companyId;
  private dbPath = "/users";
  usersRef: AngularFirestoreCollection<UserData>;

  currentUserSource = new ReplaySubject<User>(1);
  currentUser$: Observable<User> = this.currentUserSource.asObservable();

  // Save logged in user data
  userData: any;
  // Source data
  private _isLogin = new BehaviorSubject(false);
  // Observable data
  $isAuthenticated: Observable<boolean> = this._isLogin.asObservable();

  constructor(
    private http: HttpClient,
    public afAuth: AngularFireAuth,
    private db: AngularFirestore,
    private fns: AngularFireFunctions
  ) {
    this.afAuth.authState.subscribe((user) => {
      // console.log("UserService/afAuth.authState:", user);
      this.userData = user ? user : null;
      this.configureAuthState(user);
    });

    this.usersRef = db.collection(this.dbPath);
  }

  private configureAuthState(firebaseUser): void {
    // console.log("UserService/configureAuthState/:", firebaseUser);

    if (firebaseUser) {
      firebase.default
        .auth()
        .currentUser.getIdTokenResult()
        .then((idTokenResult) => {
          console.log("UserService/getIdTokenResult/:", idTokenResult);

          localStorage.setItem("token", idTokenResult.token);

          this._isLogin.next(true);

          let userFb: User = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL
              ? firebaseUser.photoURL
              : "/assets/images/default_avatar.jpg",
          };

          userFb.isPremium = !!idTokenResult.claims.isPremium;
          userFb.expirationOn = !!idTokenResult.claims.expirationOn
            ? idTokenResult.claims.expirationOn
            : new Date();
          userFb.admin = !!idTokenResult.claims.admin;
          userFb.moderator = !!idTokenResult.claims.moderator;
          userFb.product = !!idTokenResult.claims.product;
          userFb.customer = !!idTokenResult.claims.customer;
          userFb.warehouse = !!idTokenResult.claims.warehouse;
          userFb.finance = !!idTokenResult.claims.finance;
          userFb.receipt = !!idTokenResult.claims.receipt;
          userFb.purchase = !!idTokenResult.claims.purchase;
          userFb.loan = !!idTokenResult.claims.loan;
          userFb.working = !!idTokenResult.claims.working;
          userFb.salesman = !!idTokenResult.claims.salesman;
          userFb.role = !!idTokenResult.claims.role
            ? idTokenResult.claims.role
            : "freelancer";
          userFb.companyId = !!idTokenResult.claims.companyId
            ? idTokenResult.claims.companyId
            : "";
          userFb.shopId = !!idTokenResult.claims.shopId
            ? idTokenResult.claims.shopId
            : firebaseUser.uid;

          userFb.printReceiptA4 = !!idTokenResult.claims.printReceiptA4;
          userFb.printReceiptPos = !!idTokenResult.claims.printReceiptPos;
          userFb.language = !!idTokenResult.claims.language
            ? idTokenResult.claims.language
            : "vn";
          userFb.webUrl = !!idTokenResult.claims.webUrl
            ? idTokenResult.claims.webUrl
            : "";
          userFb.offlineDb = !!idTokenResult.claims.offlineDb;

          localStorage.setItem("shopId", userFb.shopId);
          // console.log("UserService/getIdTokenResult/userFb:", userFb);
          this.currentUserSource.next(userFb);

          const url = window.location.href.toLowerCase();
          if (
            !userFb.admin &&
            userFb.webUrl !== "" &&
            !url.includes(userFb.webUrl)
          ) {
            window.location.href = userFb.webUrl;
          }
        })
        .catch((error) => {
          console.log("UserService/getIdTokenResult/error:", error);
          this.doSignedOutUser();
        });
    } else {
      this.doSignedOutUser();
    }
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    const value = token !== null && token != "" ? true : false;
    //console.log("UserService/isLoggedIn():", value);
    return value;
  }

  uid(): string {
    const uid = this.userData && this.userData.uid ? this.userData.uid : "";

    return uid;
  }

  getCurrentUser(): Observable<User> {
    return this.currentUser$;
  }

  get(id: string) {
    return this.usersRef.doc(id).valueChanges();
  }

  //FireAuth
  // Sign in with email/password
  signIn(email, password) {
    // return this.afAuth
    //   .signInWithEmailAndPassword(email, password)
    //   .then((result) => {
    //     console.log("UserService/signIn:", result);
    //   })
    //   .catch((error) => {
    //     //window.alert(error.message);
    //     throw error;
    //   });
    return this.afAuth.signInWithEmailAndPassword(email, password);
  }

  signOut() {
    return this.afAuth.signOut().then(() => {
      this.doSignedOutUser();
    });
  }

  resetPassword(email) {
    return this.afAuth.sendPasswordResetEmail(email).catch((error) => {
      throw error;
    });
  }

  private doSignedOutUser() {
    localStorage.removeItem("shopId");
    localStorage.removeItem("token");
    // localStorage.clear();
    this.userData = null;
    this._isLogin.next(false);
    this.currentUserSource.next(null);
  }

  private verifyTokenAPI(theToken) {
    this.http
      .post(this.baseUrl + "/FirebaseAuth/VerifyToken", {
        token: theToken,
      })
      .subscribe({
        next: () => {},
        error: (err) => {
          this.doSignedOutUser();
        },
      });
  }

  //CRUD User

  getUsers(shopId): AngularFirestoreCollection<UserData> {
    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("shopId", "==", shopId)
        .orderBy("updatedOn", "desc")
    );
  }

  getAccounts(): AngularFirestoreCollection<UserData> {
    return this.db.collection(this.dbPath, (ref) =>
      ref.where("isDeleted", "==", false).orderBy("updatedOn", "desc")
    );
  }

  createAccount(user: User) {
    const callable = this.fns.httpsCallable("services-account-create");
    return callable({
      shopId: user.shopId,
      email: user.email,
      displayName: user.displayName,
      password: user.password,
      admin: user.admin,
      moderator: user.moderator,
      product: user.product,
      customer: user.customer,
      warehouse: user.warehouse,
      finance: user.finance,
      receipt: user.receipt,
      purchase: user.purchase,
      loan: user.loan,
      working: user.working,
      salesman: user.salesman,
      role: user.role,
      language: user.language,
      shopName: user.shopName,
    });
  }

  createUserData(uid, email, displayName) {
    const userRef: AngularFirestoreDocument<any> = this.db.doc(`users/${uid}`);
    const userData: UserData = {
      uid: uid,
      shopId: uid,
      email: email,
      displayName: displayName,
      photoURL: "/assets/images/default_avatar.jpg",
      role: "freelancer",
      coins: 0,
      stars: 0,
      hearts: 0,
      noLiked: 0,
      noProduct: 0,
      noUser: 0,
      noCustomer: 0,
      amount: 0,
      notifications: 0,
      isActive: true,
      isDeleted: false,
      updatedOn: new Date(),
    };
    userRef.set(userData, {
      merge: true,
    });

    //Sync user to local
    this.syncUser(userData);
  }

  updateUserData(data: UserData): Promise<void> {
    return this.usersRef.doc(data.uid).update({
      displayName: data.displayName ? data.displayName : "user",
      role: data.role ? data.role : "",
      updatedOn: new Date(),
    });
  }

  getToken(): string {
    return localStorage.getItem("token");
  }

  private syncUser(user) {
    if (environment.syncLive) {
      this.http.post(this.baseUrl + "/FirebaseAuth", user).subscribe({
        next: () => {},
        error: (err) => {},
      });
    }
  }

  getUserRole(uid) {
    // return this.http.get(this.baseUrl + "/FirebaseAuth/GetRole/" + uid);
    const callable = this.fns.httpsCallable("services-account-getRoles");
    return callable({
      uid: uid,
    });
  }

  setUserRole(user) {
    //return this.http.post(this.baseUrl + "/FirebaseAuth/SetRole/", user);
    const callable = this.fns.httpsCallable("services-account-setRoles");
    return callable({
      uid: user.uid,
      shopId: user.shopId,
      shopName: user.shopName,
      role: user.role,
      displayName: user.displayName,
      admin: user.admin,
      moderator: user.moderator,
      product: user.product,
      customer: user.customer,
      warehouse: user.warehouse,
      finance: user.finance,
      receipt: user.receipt,
      purchase: user.purchase,
      loan: user.loan,
      working: user.working,
      salesman: user.salesman,
      disabled: user.disabled,
      password: user.password,
      printReceiptA4: user.printReceiptA4,
      printReceiptPos: user.printReceiptPos,
      language: user.language,
      webUrl: user.webUrl,
      offlineDb: user.offlineDb,
    });
  }

  register(shop: Shop, user: User) {
    const callable = this.fns.httpsCallable("services-account-register");
    return callable({
      shopName: shop.title,
      address: shop.address,
      mobile: shop.mobile,
      description: shop.description,

      displayName: user.displayName,
      email: user.email,
      password: user.password,

      product: shop.product,
      customer: shop.customer,
      warehouse: shop.warehouse,
      finance: shop.finance,
      receipt: shop.receipt,
      purchase: shop.purchase,
      loan: shop.loan,
      working: shop.working,
      trade: shop.trade,
    });
  }

  changePassword(password) {
    console.log("services-account-changePassword:", password);
    const callable = this.fns.httpsCallable("services-account-changePassword");
    return callable({
      password: password,
    });
  }
}
